import type { SessionUser } from "@epos/contracts";
import { ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation, type NavLinkRenderProps } from "react-router-dom";
import "./legacy-shell.css";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, supportApi } from "./api";
import { hasCrmStaffPermission } from "./modules";
import { ShareScreenDialog, SubmitTicketDialog } from "./support/SupportDialogs";

export type LegacyMenuItem = {
    label: string;
    path?: string;
    icon: string;
    staffPermissions?: string[];
    staffAlways?: boolean;
    feature?: string;
    children?: LegacyMenuItem[];
};

const crmMenu: LegacyMenuItem[] = [
    { label: "Audit", path: "/audit", icon: "fas fa-history" },
    { label: "AI Support", path: "/ai-support", icon: "fas fa-comments" },
    { label: "Dashboard", path: "/", icon: "fas fa-tachometer-alt", feature: "dashboard" },
    {
        label: "Activities",
        icon: "fa fa-building",
        children: [
            {
                label: "Activities List",
                path: "/activities",
                icon: "far fa-circle", feature: "activities.list",
            },
            {
                label: "Activities Category",
                path: "/activity-categories",
                icon: "far fa-circle", feature: "activities.categories",
            },
            {
                label: "Widget booking URLs",
                path: "/activities/widget-booking-urls",
                icon: "far fa-circle", feature: "activities.list",
            },
        ],
    },
    { label: "Work Location", path: "/locations", icon: "fa fa-building", feature: "locations" },
    {
        label: "Products",
        icon: "fas fa-list",
        children: [
            { label: "Stock", path: "/stock", icon: "far fa-circle", feature: "products.stock" },
            {
                label: "Stock Category",
                path: "/stock-categories",
                icon: "far fa-circle", feature: "products.categories",
            },
            {
                label: "Manage Inventory",
                path: "/stock/inventory",
                icon: "far fa-circle", feature: "products.inventory",
            },
            {
                label: "Suppliers",
                path: "/suppliers",
                icon: "far fa-circle", feature: "products.inventory",
            },
            {
                label: "Purchase Orders",
                path: "/purchase-orders",
                icon: "far fa-circle", feature: "products.inventory",
            },
        ],
    },
    { label: "Table management", path: "/tables", icon: "fas fa-table", feature: "tables" },
    { label: "Order management", path: "/orders", icon: "fas fa-ticket-alt", feature: "orders" },
    { label: "Staff Users", path: "/staff", icon: "fas fa-user-tie", feature: "staff" },
    { label: "My plan", path: "/subscription", icon: "fas fa-receipt" },
    {
        label: "Customers",
        icon: "fa fa-users",
        children: [
            { label: "All Customers", path: "/customers", icon: "far fa-circle", feature: "customers.all" },
            { label: "Email templates", path: "/customers/email-templates", icon: "far fa-envelope", feature: "customers.all" },
            { label: "Loyalty", path: "/customers/loyalty", icon: "far fa-circle", feature: "customers.loyalty" },
            { label: "Membership", path: "/customers/membership", icon: "far fa-id-card", feature: "customers.membership" },
            { label: "Marketing", path: "/customers/marketing", icon: "far fa-envelope", feature: "customers.marketing" },
            { label: "Special events", path: "/customers/special-events", icon: "far fa-calendar-alt", feature: "customers.special-events" },
        ],
    },
    {
        label: "Payment History",
        path: "/payments", feature: "payments.history",
        icon: "fa fa-money-bill-alt",
    },
    { label: "Refunds", path: "/refunds", icon: "fa fa-undo", feature: "refunds" },
    { label: "Invoices", path: "/invoices", icon: "fa fa-file-invoice", feature: "invoices" },
    {
        label: "Waiver",
        icon: "fas fa-file-alt",
        children: [
            {
                label: "Waiver settings",
                path: "/waiver-settings",
                icon: "far fa-circle", feature: "waivers.settings",
            },
            {
                label: "Waiver management",
                path: "/waiver-management",
                icon: "far fa-circle", feature: "waivers.management",
            },
        ],
    },
    {
        label: "Payment Gateway Details",
        path: "/payment-gateway",
        icon: "fa fa-credit-card", feature: "payments.online",
    },
    { label: "Reports", path: "/reports", icon: "fa fa-file-excel", feature: "reports" },
    { label: "Settings", path: "/settings", icon: "fa fa-cog fa-gear", feature: "settings" },
    { label: "Printer Log", path: "/printer-log", icon: "fa fa-print", feature: "printing" },
];

const staffMenu: LegacyMenuItem[] = [
    {
        label: "Dashboard",
        path: "/",
        icon: "fas fa-tachometer-alt",
        staffPermissions: ["/"],
        feature: "dashboard",
    },
    {
        label: "Activities",
        icon: "fa fa-building",
        children: [
            {
                label: "Activities List",
                path: "/activities",
                icon: "far fa-circle",
                staffPermissions: ["/activities"],
                feature: "activities.list",
            },
            {
                label: "Activities Category",
                path: "/activity-categories",
                icon: "far fa-circle",
                staffPermissions: ["/activity-categories"],
                feature: "activities.categories",
            },
            {
                label: "Widget booking URLs",
                path: "/activities/widget-booking-urls",
                icon: "far fa-circle",
                staffPermissions: ["/activities"],
                feature: "activities.list",
            },
        ],
    },
    {
        label: "Work Location",
        path: "/locations",
        icon: "fa fa-building",
        staffPermissions: ["/locations"],
        feature: "locations",
    },
    {
        label: "Products",
        icon: "fas fa-list",
        children: [
            {
                label: "Stock",
                path: "/stock",
                icon: "far fa-circle",
                staffPermissions: ["/stock/items"],
                feature: "products.stock",
            },
            {
                label: "Stock Category",
                path: "/stock-categories",
                icon: "far fa-circle",
                staffPermissions: ["/stock-categories"],
                feature: "products.categories",
            },
            {
                label: "Manage Inventory",
                path: "/stock/inventory",
                icon: "far fa-circle",
                staffPermissions: ["/stock/inventory"],
                feature: "products.inventory",
            },
            {
                label: "Suppliers",
                path: "/suppliers",
                icon: "far fa-circle",
                staffPermissions: ["/stock/inventory"],
                feature: "products.inventory",
            },
            {
                label: "Purchase Orders",
                path: "/purchase-orders",
                icon: "far fa-circle",
                staffPermissions: ["/stock/inventory"],
                feature: "products.inventory",
            },
        ],
    },
    {
        label: "Order management",
        path: "/orders",
        icon: "fas fa-ticket-alt",
        staffPermissions: ["/orders"],
        feature: "orders",
    },
    {
        label: "Customers",
        icon: "fa fa-users",
        children: [
            { label: "All Customers", path: "/customers", icon: "far fa-circle", staffPermissions: ["/customers/all"], feature: "customers.all" },
            { label: "Loyalty", path: "/customers/loyalty", icon: "far fa-circle", staffPermissions: ["/customers/loyalty"], feature: "customers.loyalty" },
            { label: "Membership", path: "/customers/membership", icon: "far fa-id-card", staffPermissions: ["/customers/membership"], feature: "customers.membership" },
            { label: "Marketing", path: "/customers/marketing", icon: "far fa-envelope", staffPermissions: ["/customers/marketing"], feature: "customers.marketing" },
            { label: "Special events", path: "/customers/special-events", icon: "far fa-calendar-alt", staffPermissions: ["/customers/special-events"], feature: "customers.special-events" },
        ],
    },
    {
        label: "Payment History",
        path: "/payments",
        icon: "fa fa-money-bill-alt",
        staffPermissions: ["/payments/history"],
        feature: "payments.history",
    },
    {
        label: "Refunds",
        path: "/refunds",
        icon: "fa fa-undo",
        staffPermissions: ["/refunds"],
        feature: "refunds",
    },
    {
        label: "Invoices",
        path: "/invoices",
        icon: "fa fa-file-invoice",
        staffPermissions: ["/invoices", "/invoice"],
        feature: "invoices",
    },
    {
        label: "Waiver",
        icon: "fas fa-file-alt",
        children: [
            { label: "Waiver settings", path: "/waiver-settings", icon: "far fa-circle", staffPermissions: ["/waiver-settings"], feature: "waivers.settings" },
            { label: "Waiver management", path: "/waiver-management", icon: "far fa-circle", staffPermissions: ["/waiver-management"], feature: "waivers.management" },
        ],
    },
    {
        label: "Reviews",
        path: "/reviews",
        icon: "fa fa-star",
        staffPermissions: ["/reviews"],
        feature: "reviews",
    },
    {
        label: "Pos",
        path: "/pos",
        icon: "fa fa-user-plus",
        staffPermissions: ["/pos"],
        feature: "pos",
    },
    {
        label: "Table management",
        path: "/tables",
        icon: "fas fa-table",
        staffPermissions: ["/tables"],
        feature: "tables",
    },
    {
        label: "Kiosk",
        path: "/kiosk",
        icon: "fa fa-user-plus",
        staffPermissions: ["/kiosk/self-service"],
        feature: "kiosk",
    },
    {
        label: "KDS",
        path: "/kds",
        icon: "fa fa-user-plus",
        staffPermissions: ["/kds"],
        feature: "kds",
    },
    {
        label: "Reports",
        path: "/reports",
        icon: "fa fa-file-excel",
        staffPermissions: ["/reports"],
        feature: "reports",
    },
    {
        label: "Settings",
        path: "/settings",
        icon: "fa fa-cog fa-gear",
        staffPermissions: [
            "/settings/general", "/settings/devices", "/settings/conditions", "/settings/legal",
            "/settings/security", "/settings/smtp", "/settings/jobs",
        ],
        feature: "settings",
    },
    {
        label: "Printer Log",
        path: "/printer-log",
        icon: "fa fa-print",
        staffPermissions: ["/printer-log"],
        feature: "printing",
    },
    {
        label: "Payment Gateway Details",
        path: "/payment-gateway",
        icon: "fa fa-credit-card",
        staffPermissions: ["/payment-gateway"],
        feature: "payments.online",
    },
];

const superAdminMenu: LegacyMenuItem[] = [
    { label: "Dashboard", path: "/", icon: "fas fa-tachometer-alt" },
    {
        label: "Staff Users",
        icon: "fas fa-user-tie",
        children: [
            {
                label: "All Staff Users",
                path: "/admin/staff",
                icon: "far fa-circle",
            },
        ],
    },
    {
        label: "CRM Admins",
        icon: "fas fa-user",
        children: [
            {
                label: "All CRM Admins",
                path: "/admin/clients",
                icon: "far fa-circle",
            },
            {
                label: "Subscription Records",
                path: "/admin/subscription-requests",
                icon: "far fa-circle",
            },
        ],
    },
    {
        label: "Inquiries",
        icon: "fa fa-question-circle",
        children: [
            {
                label: "Inquiries Listing",
                path: "/admin/inquiries",
                icon: "far fa-circle",
            },
        ],
    },
    {
        label: "Invoices",
        icon: "fa fa-file-invoice",
        children: [
            {
                label: "Invoice Listing",
                path: "/admin/invoices",
                icon: "far fa-circle",
            },
            {
                label: "Invoice Layout",
                path: "/admin/invoices/layout",
                icon: "far fa-circle",
            },
        ],
    },
    {
        label: "Email Template",
        icon: "fa fa-envelope",
        children: [
            {
                label: "Template Listing",
                path: "/admin/email-templates",
                icon: "far fa-circle",
            },
        ],
    },
    {
        label: "All Payments",
        path: "/admin/payments",
        icon: "far fa-money-bill-alt",
    },
    { label: "Marketing-CMS", path: "/admin/pages", icon: "fas fa-file-alt" },
    { label: "Admin Charge", path: "/admin/charge", icon: "fas fa-chart-bar" },
    { label: "Client email", path: "/admin/smtp-settings", icon: "fas fa-envelope-open-text" },
    { label: "Platform AI", path: "/admin/ai-settings", icon: "fas fa-wand-magic-sparkles" },
    { label: "Printer Log", path: "/admin/printer-log", icon: "fa fa-print" },
    { label: "Support Tickets", path: "/admin/support-tickets", icon: "fa fa-life-ring" },
    { label: "Remote Support", path: "/admin/support-sessions", icon: "fa fa-desktop" },
    {
        label: "Developers",
        icon: "fa fa-code",
        children: [
            { label: "Customer API", path: "/admin/developers", icon: "far fa-circle" },
        ],
    },
];

const superStaffMenu: LegacyMenuItem[] = [
    {
        label: "Dashboard",
        path: "/",
        icon: "fas fa-tachometer-alt",
        staffPermissions: ["/"],
    },
    {
        label: "CRM Admins",
        icon: "fas fa-user",
        children: [
            {
                label: "All CRM Admins",
                path: "/admin/clients",
                icon: "far fa-circle",
                staffPermissions: ["/admin/clients"],
            },
            {
                label: "Subscription Records",
                path: "/admin/subscription-requests",
                icon: "far fa-circle",
                staffPermissions: ["/admin/subscriptions"],
            },
        ],
    },
    {
        label: "Platform AI",
        path: "/admin/ai-settings",
        icon: "fas fa-wand-magic-sparkles",
        staffPermissions: ["/admin/ai-settings"],
    },
    {
        label: "Inquiries",
        icon: "fa fa-question-circle",
        children: [
            {
                label: "Inquiries Listing",
                path: "/admin/inquiries",
                icon: "far fa-circle",
                staffPermissions: ["/admin/inquiries"],
            },
        ],
    },
    {
        label: "Invoices",
        icon: "fa fa-file-invoice",
        children: [
            {
                label: "Invoice Listing",
                path: "/admin/invoices",
                icon: "far fa-circle",
                staffPermissions: ["/admin/invoices"],
            },
            {
                label: "Invoice Layout",
                path: "/admin/invoices/layout",
                icon: "far fa-circle",
                staffPermissions: ["/admin/invoices"],
            },
        ],
    },
    {
        label: "Email Template",
        icon: "fa fa-envelope",
        children: [
            {
                label: "Template Listing",
                path: "/admin/email-templates",
                icon: "far fa-circle",
                staffPermissions: ["/admin/email-templates"],
            },
        ],
    },
    {
        label: "All Payments",
        path: "/admin/payments",
        icon: "far fa-money-bill-alt",
        staffPermissions: ["/admin/payments"],
    },
    {
        label: "Marketing-CMS",
        path: "/admin/pages",
        icon: "fas fa-file-alt",
        staffPermissions: ["/admin/pages"],
    },
    {
        label: "Admin Charge",
        path: "/admin/charge",
        icon: "fas fa-chart-bar",
        staffPermissions: ["/admin/charge"],
    },
    {
        label: "Support Tickets",
        path: "/admin/support-tickets",
        icon: "fa fa-life-ring",
        staffPermissions: ["/admin/support-tickets"],
    },
    {
        label: "Remote Support",
        path: "/admin/support-sessions",
        icon: "fa fa-desktop",
        staffPermissions: ["/admin/support-sessions"],
    },
];

function isCurrent(pathname: string, item: LegacyMenuItem): boolean {
    if (item.path === "/") return pathname === "/";
    if (item.path && pathname.startsWith(item.path)) return true;
    return item.children?.some((child) => isCurrent(pathname, child)) ?? false;
}

function allowed(user: SessionUser, item: LegacyMenuItem, features?: Record<string,{enabled:boolean;visible:boolean}>): boolean {
    if (item.feature && features?.[item.feature]?.visible === false) return false;
    if (
        user.userType === 2 &&
        item.path &&
        ["/pos", "/kiosk", "/kds"].includes(item.path)
    )
        return false;
    if (user.userType === 5) {
        if (
            user.permissions.includes("*") || item.staffAlways
        )
            return true;
        if (
            item.staffPermissions?.some((permission) => hasCrmStaffPermission(user.permissions, permission))
        )
            return true;
        return item.children?.some((child) => allowed(user, child, features)) ?? false;
    }
    if (user.userType !== 4) return true;
    if (user.permissions.includes("*")) return true;
    if (
        item.staffPermissions?.some((permission) =>
            user.permissions.includes(permission),
        )
    )
        return true;
    if (item.path)
        return user.permissions.some(
            (permission) =>
                permission !== "/" &&
                (item.path!.startsWith(permission) ||
                    permission.startsWith(item.path!)),
        );
    return item.children?.some((child) => allowed(user, child, features)) ?? false;
}

function MenuNode({
    item,
    user,
    close,
    openMenu,
    setOpenMenu,
    features,
}: {
    item: LegacyMenuItem;
    user: SessionUser;
    close(): void;
    openMenu: string | null;
    setOpenMenu(label: string | null): void;
    features?: Record<string,{enabled:boolean;visible:boolean}>;
}) {
    const { pathname } = useLocation();
    const current = isCurrent(pathname, item);
    const open = openMenu === item.label;
    useEffect(() => {
        if (current && item.children) setOpenMenu(item.label);
    }, [current, item.children, item.label, setOpenMenu]);
    if (!allowed(user, item, features)) return null;
    if (item.children)
        return (
            <li className={`legacy-nav-item ${open ? "menu-open" : ""}`}>
                <button
                    type="button"
                    className={`legacy-nav-link ${current ? "active" : ""}`}
                    onClick={() => setOpenMenu(open ? null : item.label)}
                    aria-expanded={open}
                >
                    <i
                        className={`legacy-nav-icon ${item.icon}`}
                        aria-hidden="true"
                    />
                    <p>
                        {item.label}
                        <i className="fas fa-angle-left legacy-right" />
                    </p>
                </button>
                <ul className="legacy-treeview">
                    {item.children
                        .filter((child) => allowed(user, child, features))
                        .map((child) => (
                            <li className="legacy-nav-item" key={child.label}>
                                <NavLink
                                    className={({ isActive }: NavLinkRenderProps) =>
                                        `legacy-nav-link ${isActive ? "active" : ""}`
                                    }
                                    to={child.path!}
                                    onClick={close}
                                >
                                    <i
                                        className={`legacy-nav-icon ${child.icon}`}
                                        aria-hidden="true"
                                    />
                                    <p>{child.label}</p>
                                </NavLink>
                            </li>
                        ))}
                </ul>
            </li>
        );
    return (
        <li className="legacy-nav-item">
            <NavLink
                className={({ isActive }: NavLinkRenderProps) =>
                    `legacy-nav-link ${isActive ? "active" : ""}`
                }
                to={item.path!}
                end={item.path === "/"}
                onClick={close}
            >
                <i
                    className={`legacy-nav-icon ${item.icon}`}
                    aria-hidden="true"
                />
                <p>{item.label}</p>
            </NavLink>
        </li>
    );
}

export function LegacyShell({
    user,
    logout,
    children,
}: {
    user: SessionUser;
    logout(): void;
    children: ReactNode;
}) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [supportDialog, setSupportDialog] = useState<"ticket" | "share" | null>(null);
    const [screenShareActive, setScreenShareActive] = useState(false);
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
    useEffect(() => {
        const updateFullscreen = () => setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener("fullscreenchange", updateFullscreen);
        return () => document.removeEventListener("fullscreenchange", updateFullscreen);
    }, []);
    const [navigationReady, setNavigationReady] = useState(false);
    useEffect(() => {
        const timer = window.setTimeout(() => setNavigationReady(true), 250);
        return () => window.clearTimeout(timer);
    }, []);
    const subscription = useQuery({queryKey:["my-subscription-navigation"],queryFn:api.mySubscription.navigation,enabled:navigationReady&&[2,5].includes(user.userType),staleTime:30_000});
    const supportNotifications = useQuery({queryKey:["platform-support-notifications"],queryFn:supportApi.notifications,enabled:navigationReady&&[3,4].includes(user.userType),refetchInterval:30_000});
    const unreadSupport = supportNotifications.data?.filter(item => !item.isRead).length ?? 0;
    const directDebitSetup = useMutation({
        mutationFn: api.mySubscription.beginDirectDebit,
        onSuccess: state => { if (state.redirectUrl) window.location.assign(state.redirectUrl); },
    });
    const features = subscription.data?.features;
    const access = subscription.data?.access;
    const collectionDate = access?.firstCollectionDate
        ? new Date(`${access.firstCollectionDate}T12:00:00`).toLocaleDateString("en-GB")
        : "";
    const menu =
        user.userType === 3
            ? superAdminMenu
            : user.userType === 4
              ? superStaffMenu
              : user.userType === 5
                ? staffMenu
                : crmMenu;
    return (
        <div className={`legacy-wrapper ${sidebarOpen ? "sidebar-open" : ""}`}>
            <nav className="legacy-main-header">
                <ul className="legacy-navbar-nav">
                    <li>
                        <button
                            className="legacy-header-link"
                            onClick={() => setSidebarOpen((value) => !value)}
                            aria-label="Toggle navigation"
                        >
                            <i className="fas fa-bars" />
                        </button>
                    </li>
                    <li className="legacy-hub-link-item">
                        <Link className="legacy-header-link" to="/">
                            Home
                        </Link>
                    </li>
                </ul>
                <ul className="legacy-navbar-nav legacy-navbar-right">
                    <li className="legacy-account-menu">
                        <button
                            className="legacy-header-link legacy-account-toggle"
                            onClick={() => setAccountOpen((value) => !value)}
                        >
                            {user.name} <span className="legacy-caret" />
                        </button>
                        {accountOpen && (
                            <div className="legacy-dropdown">
                                <button onClick={logout}>Logout</button>
                                <Link
                                    to="/profile"
                                    onClick={() => setAccountOpen(false)}
                                >
                                    My Profile
                                </Link>
                                <button onClick={() => { setAccountOpen(false); setSupportDialog("share"); }}>{screenShareActive ? "Screen sharing active" : "Share screen"}</button>
                                {[2,5].includes(user.userType) && <button onClick={() => { setAccountOpen(false); setSupportDialog("ticket"); }}>Submit / View Tickets</button>}
                                {[3,4].includes(user.userType) && <Link to="/admin/support-tickets" onClick={() => setAccountOpen(false)}>Support tickets{unreadSupport ? ` (${unreadSupport})` : ""}</Link>}
                            </div>
                        )}
                    </li>
                    <li>
                        <button
                            className="legacy-header-link legacy-fullscreen-toggle"
                            onClick={() =>
                                document.fullscreenElement
                                    ? document.exitFullscreen()
                                    : document.documentElement.requestFullscreen()
                            }
                            aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
                            title={isFullscreen ? "Exit full screen" : "Full screen"}
                        >
                            <i className={isFullscreen ? "fas fa-compress-arrows-alt" : "fas fa-expand-arrows-alt"} aria-hidden="true" />
                            <span>{isFullscreen ? "Exit full screen" : "Full screen"}</span>
                        </button>
                    </li>
                </ul>
            </nav>
            <aside className="legacy-main-sidebar">
                <Link to="/home" className="legacy-brand-link" title="EPOS Bookings home">
                    <img
                        src={user.crmLogo || "/logo-square-dark.png"}
                        alt="Site Logo"
                    />
                    <span>EPOS</span>
                </Link>
                <div className="legacy-sidebar-inner">
                    <div className="legacy-user-panel">
                        <img
                            src={user.profileImageUrl || "/user2-160x160.jpg"}
                            alt="User"
                        />
                        <Link to="/profile">{user.name}</Link>
                    </div>
                    <nav className="legacy-sidebar-nav">
                        <ul>
                            {menu.map((item) => (
                                <MenuNode
                                    item={item}
                                    user={user}
                                    close={() => setSidebarOpen(false)}
                                    openMenu={openMenu}
                                    setOpenMenu={setOpenMenu}
                                    features={features}
                                    key={item.label}
                                />
                            ))}
                        </ul>
                    </nav>
                </div>
            </aside>
            {supportDialog === "ticket" && <SubmitTicketDialog onClose={() => setSupportDialog(null)} />}
            <ShareScreenDialog open={supportDialog === "share"} onClose={() => setSupportDialog(null)} onActiveChange={setScreenShareActive} />
            <div className="legacy-content-wrapper">
                {access && access.state !== "not_configured" && access.state !== "not_scheduled" && (
                    <aside className={`subscription-access-banner ${access.accessAllowed ? "trial" : "blocked"}`}>
                        <div>
                            <strong>{access.accessAllowed ? access.trialActive ? "Free trial" : "Subscription" : "Direct Debit required"}</strong>
                            <span>{access.accessAllowed
                                ? access.trialActive
                                    ? `${Math.max(0, access.daysUntilFirstCollection ?? 0)} days remaining · first collection ${collectionDate}`
                                    : `First collection ${collectionDate}`
                                : `The Direct Debit setup deadline has passed. ${user.userType === 2 ? "Add bank details to restore access." : "Ask your CRM administrator to set up Direct Debit."}`}</span>
                        </div>
                        {user.userType === 2 && (access.directDebitConfigured
                            ? <Link to="/subscription">View plan</Link>
                            : <button disabled={directDebitSetup.isPending} onClick={() => directDebitSetup.mutate()}>{directDebitSetup.isPending ? "Opening GoCardless…" : "Provide bank details"}</button>)}
                        {directDebitSetup.isError && <span className="subscription-access-error">{directDebitSetup.error.message}</span>}
                    </aside>
                )}
                <main>{children}</main>
            </div>
            {sidebarOpen && (
                <button
                    className="legacy-sidebar-scrim"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Close navigation"
                />
            )}
        </div>
    );
}

export const legacyPlaceholderRoutes = Array.from(
    new Set(
        [...crmMenu, ...superAdminMenu]
            .flatMap((item) => item.children ?? [item])
            .map((item) => item.path)
            .filter((path): path is string => Boolean(path)),
    ),
);
